import axios from "axios";

const normalizeBase = (b) => String(b || "").trim().replace(/\/+$/, "");
const API_BASE = normalizeBase(import.meta.env.VITE_API_BASE_URL || "");

async function request(method, path, data) {
  const response = await axios({
    method,
    url: `${API_BASE}${path}`,
    data,
    timeout: 180000,
    validateStatus: (status) => status >= 200 && status < 600,
  });

  if (response.status === 404 && ['/api/comercial/romaneio-entrega/consultas', '/api/comercial/romaneio-entrega/transportadora/consultas'].includes(path)) {
    throw new Error("A nova consulta do Romaneio ainda não está disponível no backend em execução. Atualize e recrie o container da API com os arquivos do pacote. Recompilar somente o frontend não atualiza estas rotas.");
  }
  if (response.status >= 400) {
    throw new Error(response?.data?.message || response?.data?.error || (response.status === 502 || response.status === 504 ? "O servidor de consulta não respondeu. Confira se o backend está ativo e tente novamente." : `Erro HTTP ${response.status}`));
  }

  const payload = response.data || {};
  if (payload.ok === false) {
    throw new Error(payload.message || "Erro ao processar solicitação.");
  }

  return payload.data ?? payload;
}

export const fetchCommercialSnapshot = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      search.set(key, String(value).trim());
    }
  });
  const qs = search.toString();
  return request("get", `/api/comercial/snapshot${qs ? `?${qs}` : ""}`);
};

export const saveSeller = (payload) =>
  payload?.id
    ? request("put", `/api/comercial/sellers/${payload.id}`, payload)
    : request("post", "/api/comercial/sellers", payload);

export const deleteSeller = (id) => request("delete", `/api/comercial/sellers/${id}`);

export const saveDailyRecord = (payload) =>
  payload?.id
    ? request("put", `/api/comercial/daily-records/${payload.id}`, payload)
    : request("post", "/api/comercial/daily-records", payload);

export const deleteDailyRecord = (id) => request("delete", `/api/comercial/daily-records/${id}`);

export const saveFuelVehicle = (payload) =>
  payload?.id
    ? request("put", `/api/comercial/fuel-vehicles/${payload.id}`, payload)
    : request("post", "/api/comercial/fuel-vehicles", payload);

export const deleteFuelVehicle = (id) => request("delete", `/api/comercial/fuel-vehicles/${id}`);

export const saveFuelEntry = (payload) =>
  payload?.id
    ? request("put", `/api/comercial/fuel-entries/${payload.id}`, payload)
    : request("post", "/api/comercial/fuel-entries", payload);

export const deleteFuelEntry = (id) => request("delete", `/api/comercial/fuel-entries/${id}`);

export const savePromoterStore = (payload) =>
  payload?.id
    ? request("put", `/api/comercial/promoter-stores/${payload.id}`, payload)
    : request("post", "/api/comercial/promoter-stores", payload);

export const deletePromoterStore = (id) => request("delete", `/api/comercial/promoter-stores/${id}`);

export const resolvePromoterReplacement = (id, payload) =>
  request("post", `/api/comercial/promoter-stores/${id}/resolver-troca`, payload);

export const savePromoterRecord = (payload) =>
  payload?.id
    ? request("put", `/api/comercial/promoter-records/${payload.id}`, payload)
    : request("post", "/api/comercial/promoter-records", payload);

export const deletePromoterRecord = (id) => request("delete", `/api/comercial/promoter-records/${id}`);

export const saveCommercialRoute = (payload) =>
  payload?.id
    ? request("put", `/api/comercial/routes/${payload.id}`, payload)
    : request("post", "/api/comercial/routes", payload);

export const deleteCommercialRoute = (id) => request("delete", `/api/comercial/routes/${id}`);

export const fetchDailyCommercialSalesSummary = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      search.set(key, String(value).trim());
    }
  });
  const qs = search.toString();
  return request("get", `/api/comercial/daily-sales-summary${qs ? `?${qs}` : ""}`);
};

export const fetchCommercialEgestorOverview = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      search.set(key, String(value).trim());
    }
  });
  const qs = search.toString();
  return request("get", `/api/comercial/egestor-visao-geral${qs ? `?${qs}` : ""}`);
};

export async function waitForCommercialJob(startPath, params, pollingPath) {
  let { job } = await request("post", startPath, params);
  const deadline = Date.now() + 12 * 60000;
  while (job.status === "running") {
    if (Date.now() > deadline) throw new Error("A consulta ainda está em andamento. Aguarde um pouco e tente buscar novamente.");
    await new Promise(resolve => setTimeout(resolve, 1500));
    ({ job } = await request("get", `${pollingPath}/${job.id}`));
  }
  if (job.status !== "completed") throw new Error(job.error || "Não foi possível concluir a consulta.");
  return job.result;
}
export const fetchDeliveryManifest = params => waitForCommercialJob("/api/comercial/romaneio-entrega/consultas", params, "/api/comercial/romaneio-entrega/consultas");

export const saveManifestDeliveryManual = (payload) =>
  request("put", "/api/comercial/romaneio-entrega/manual", payload);

export const fetchManifestTracking = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      search.set(key, String(value).trim());
    }
  });
  const qs = search.toString();
  return request("get", `/api/comercial/romaneio-entrega/rastreio${qs ? `?${qs}` : ""}`);
};

export const refreshManifestTrackingData = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") search.set(key, String(value).trim());
  });
  const qs = search.toString();
  return request("get", `/api/comercial/romaneio-entrega/rastreio/atualizar${qs ? `?${qs}` : ""}`);
};

export const fetchTotalExpressTracking = (trackingCode) =>
  request("post", "/api/comercial/romaneio-entrega/rastreio/total-express", { trackingCode });

const appendSearchParams = (path, params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      search.set(key, String(value).trim());
    }
  });
  const qs = search.toString();
  return `${API_BASE}${path}${qs ? `?${qs}` : ""}`;
};

export const buildSswTrackingUrl = (params = {}) =>
  appendSearchParams("/api/comercial/romaneio-entrega/rastreio/ssw", params);

export const buildTotalExpressTrackingUrl = (trackingCode = "") => {
  const url = new URL("https://totalconecta.totalexpress.com.br/rastreamento");
  if (String(trackingCode || "").trim()) url.searchParams.set("codigo", String(trackingCode).trim());
  return url.toString();
};
